import { BindingError } from "../BindingError.js";
import { PRIMARY_KEY_NAME } from "../bindingTypes.js";
import { TreeParameterMerger } from "./TreeParameterMerger.js";
import { MarkerTreeRoot } from "../markers/MarkerTreeRoot.js";
import { FieldMarker } from "../markers/FieldMarker.js";
import { HasOneRelationMarker } from "../markers/HasOneRelationMarker.js";
import { HasManyRelationMarker } from "../markers/HasManyRelationMarker.js";
import { EntitySubTreeMarker } from "../markers/EntitySubTreeMarker.js";
import { EntityListSubTreeMarker } from "../markers/EntityListSubTreeMarker.js";
import { assertNever } from "../utils/assertNever.js";
import { EntityFieldMarkersContainer } from "../markers/EntityFieldMarkersContainer.js";
class MarkerMerger {
  // This method assumes their placeholder names are the same
  static mergeMarkers(original, fresh) {
    if (original === fresh) {
      return original;
    }
    if (original instanceof FieldMarker) {
      if (fresh instanceof FieldMarker) {
        return this.mergeFieldMarkers(original, fresh);
      } else if (fresh instanceof HasOneRelationMarker || fresh instanceof HasManyRelationMarker) {
        return this.rejectRelationScalarCombo(original.fieldName);
      } else if (fresh instanceof EntitySubTreeMarker || fresh instanceof EntityListSubTreeMarker) {
        throw new BindingError("Merging fields and sub trees is an undefined operation.");
      }
      assertNever();
    } else if (original instanceof HasOneRelationMarker) {
      if (fresh instanceof HasOneRelationMarker) {
        return this.mergeHasOneRelationMarkers(original, fresh);
      } else if (fresh instanceof FieldMarker) {
        return this.rejectRelationScalarCombo(original.parameters.field);
      } else if (fresh instanceof HasManyRelationMarker) {
        throw new BindingError();
      } else if (fresh instanceof EntitySubTreeMarker || fresh instanceof EntityListSubTreeMarker) {
        throw new BindingError("MarkerTreeGenerator merging: SubTreeMarkers can only be merged with other sub trees.");
      }
      assertNever();
    } else if (original instanceof HasManyRelationMarker) {
      if (fresh instanceof HasManyRelationMarker) {
        return this.mergeHasManyRelationMarkers(original, fresh);
      } else if (fresh instanceof FieldMarker) {
        return this.rejectRelationScalarCombo(original.parameters.field);
      } else if (fresh instanceof HasOneRelationMarker) {
        throw new BindingError();
      } else if (fresh instanceof EntitySubTreeMarker || fresh instanceof EntityListSubTreeMarker) {
        throw new BindingError("MarkerTreeGenerator merging: SubTreeMarkers can only be merged with other sub trees.");
      }
      assertNever();
    } else if (original instanceof EntitySubTreeMarker) {
      if (fresh instanceof EntitySubTreeMarker) {
        return this.mergeEntitySubTreeMarkers(original, fresh);
      } else {
        throw new BindingError(
          "MarkerTreeGenerator merging: EntitySubTreeMarker can only be merged with other entity sub trees."
        );
      }
    } else if (original instanceof EntityListSubTreeMarker) {
      if (fresh instanceof EntityListSubTreeMarker) {
        return this.mergeEntityListSubTreeMarkers(original, fresh);
      } else {
        throw new BindingError(
          "MarkerTreeGenerator merging: EntityListSubTreeMarker can only be merged with other list sub trees."
        );
      }
    }
    assertNever();
  }
  static mergeMarkerTreeRoots(original, fresh) {
    if (original === fresh) {
      return original;
    }
    return new MarkerTreeRoot(
      this.mergeEntityFields(original.subTrees, fresh.subTrees),
      this.mergeSubTreePlaceholdersByAliases(original.placeholdersByAliases, fresh.placeholdersByAliases)
    );
  }
  static mergeSubTreeMarkers(original, fresh) {
    if (original === fresh) {
      return original;
    }
    if (original === void 0) {
      return fresh;
    }
    if (fresh === void 0) {
      return original;
    }
    const newOriginal = new Map(original);
    for (const [placeholderName, fromFresh] of fresh) {
      const fromOriginal = newOriginal.get(placeholderName);
      newOriginal.set(
        placeholderName,
        fromOriginal === void 0 ? fromFresh : fromOriginal instanceof EntityListSubTreeMarker ? MarkerMerger.mergeEntityListSubTreeMarkers(fromOriginal, fromFresh) : MarkerMerger.mergeEntitySubTreeMarkers(fromOriginal, fromFresh)
      );
    }
    return newOriginal;
  }
  static mergeSubTreePlaceholdersByAliases(original, fresh) {
    const newOriginalAliases = new Map(original);
    for (const [alias, placeholder] of fresh) {
      const fromOriginal = newOriginalAliases.get(alias);
      if (fromOriginal === void 0) {
        newOriginalAliases.set(alias, placeholder);
      } else if (fromOriginal === placeholder)
        ;
      else {
        throw new BindingError(`Illegal subTree alias '${alias}' points to distinct subTrees.`);
      }
    }
    return newOriginalAliases;
  }
  static mergeEntityFields(original, fresh) {
    if (original === fresh) {
      return original;
    }
    const newOriginal = new Map(original);
    for (const [placeholderName, freshMarker] of fresh) {
      const markerFromOriginal = newOriginal.get(placeholderName);
      newOriginal.set(
        placeholderName,
        markerFromOriginal === void 0 ? freshMarker : this.mergeMarkers(markerFromOriginal, freshMarker)
      );
    }
    return newOriginal;
  }
  static mergeEntityFieldPlaceholders(original, fresh) {
    if (original === fresh) {
      return original;
    }
    const newOriginal = new Map(original);
    for (const [fieldName, freshPlaceholders] of fresh) {
      const placeholderFromOriginal = newOriginal.get(fieldName);
      if (placeholderFromOriginal === void 0) {
        newOriginal.set(fieldName, freshPlaceholders);
      } else if (placeholderFromOriginal instanceof Set) {
        const combinedSet = new Set(placeholderFromOriginal);
        if (freshPlaceholders instanceof Set) {
          for (const freshPlaceholder of freshPlaceholders) {
            combinedSet.add(freshPlaceholder);
          }
        } else if (typeof freshPlaceholders === "string") {
          combinedSet.add(freshPlaceholders);
        } else {
          assertNever();
        }
        newOriginal.set(fieldName, combinedSet);
      } else if (typeof placeholderFromOriginal === "string") {
        if (typeof freshPlaceholders === "string") {
          if (placeholderFromOriginal === freshPlaceholders)
            ;
          else {
            newOriginal.set(fieldName, /* @__PURE__ */ new Set([placeholderFromOriginal, freshPlaceholders]));
          }
        } else if (freshPlaceholders instanceof Set) {
          if (freshPlaceholders.has(placeholderFromOriginal)) {
            newOriginal.set(fieldName, freshPlaceholders);
          } else {
            const combinedSet = new Set(freshPlaceholders);
            combinedSet.add(placeholderFromOriginal);
            newOriginal.set(fieldName, combinedSet);
          }
        } else {
          assertNever();
        }
      } else {
        assertNever();
      }
    }
    return newOriginal;
  }
  static mergeEntityFieldsContainers(original, fresh) {
    if (original === fresh) {
      return original;
    }
    return new EntityFieldMarkersContainer(
      original.hasAtLeastOneBearingField || fresh.hasAtLeastOneBearingField,
      this.mergeEntityFields(original.markers, fresh.markers),
      this.mergeEntityFieldPlaceholders(original.placeholders, fresh.placeholders)
    );
  }
  static mergeHasOneRelationMarkers(original, fresh) {
    if (original === fresh) {
      return original;
    }
    return new HasOneRelationMarker(
      TreeParameterMerger.mergeHasOneRelationsWithSamePlaceholders(original.parameters, fresh.parameters),
      this.mergeEntityFieldsContainers(original.fields, fresh.fields),
      this.mergeEnvironments(original.environment, fresh.environment)
    );
  }
  static mergeHasManyRelationMarkers(original, fresh) {
    if (original === fresh) {
      return original;
    }
    return new HasManyRelationMarker(
      TreeParameterMerger.mergeHasManyRelationsWithSamePlaceholders(original.parameters, fresh.parameters),
      this.mergeEntityFieldsContainers(original.fields, fresh.fields),
      this.mergeEnvironments(original.environment, fresh.environment)
    );
  }
  static mergeEntitySubTreeMarkers(original, fresh) {
    if (original === fresh) {
      return original;
    }
    return new EntitySubTreeMarker(
      TreeParameterMerger.mergeEntitySubTreeParametersWithSamePlaceholders(original.parameters, fresh.parameters),
      this.mergeEntityFieldsContainers(original.fields, fresh.fields),
      this.mergeEnvironments(original.environment, fresh.environment)
    );
  }
  static mergeEntityListSubTreeMarkers(original, fresh) {
    if (original === fresh) {
      return original;
    }
    return new EntityListSubTreeMarker(
      TreeParameterMerger.mergeEntityListSubTreeParametersWithSamePlaceholders(original.parameters, fresh.parameters),
      this.mergeEntityFieldsContainers(original.fields, fresh.fields),
      this.mergeEnvironments(original.environment, fresh.environment)
    );
  }
  static mergeFieldMarkers(original, fresh) {
    if (original === fresh) {
      return original;
    }
    if (original.defaultValue !== void 0 && fresh.defaultValue !== void 0 && original.defaultValue !== fresh.defaultValue) {
      throw new BindingError(`MarkerTreeGenerator merging: multiple fields "${original.fieldName}" with different defaultValue found: ${JSON.stringify([original.defaultValue, fresh.defaultValue])}, `);
    }
    return new FieldMarker(
      original.fieldName,
      original.defaultValue ?? fresh.defaultValue,
      original.isNonbearing && fresh.isNonbearing
    );
  }
  static mergeInSystemFields(original) {
    const primaryKey = new FieldMarker(PRIMARY_KEY_NAME, void 0, true);
    const freshFields = new EntityFieldMarkersContainer(
      false,
      /* @__PURE__ */ new Map([[primaryKey.placeholderName, primaryKey]]),
      /* @__PURE__ */ new Map([[PRIMARY_KEY_NAME, primaryKey.placeholderName]])
    );
    return original ? this.mergeEntityFieldsContainers(original, freshFields) : freshFields;
  }
  static mergeEnvironments(original, fresh) {
    return original.merge(fresh);
  }
  static rejectRelationScalarCombo(fieldName) {
    throw new BindingError(`Cannot combine a relation with a scalar field '${fieldName}'.`);
  }
}
export {
  MarkerMerger
};
//# sourceMappingURL=MarkerMerger.js.map
